import { DataTable, Separator } from "@dune/ui";
import { formatSimpleDate } from "@dune/utils";
import { Construction, FileWarning } from "lucide-react";
import React from "react";
import { useParams } from "react-router-dom";
import { getDocumentUrl } from "../../../../../api/queries/files.queries";
import {
	useGetBrokerUsers,
	useGetSocietyFiles,
} from "../../../../../api/queries/societies.queries";
import { useGetBrokerProducts } from "../../../../../api/queries/useGetBrokerProducts";
import { useDocumentStore } from "../../../../../stores/document.store";
import { contributorAgreementColumns } from "./agreementTabsFiles/contributorAgreementColumns";
import { partnershipHistoryColumns } from "./agreementTabsFiles/partnershipHistoryColumns";
import { agreementProductsColumns } from "./agreementTabsFiles/productColumns";

const AgreementTab: React.FC = () => {
	const { id } = useParams<{ id: string }>();
	const brokerId = id && !Number.isNaN(Number(id)) ? Number(id) : -1;
	const selectDocument = useDocumentStore((state) => state.selectDocument);

	const { data: products, isLoading: isLoadingProducts } =
		useGetBrokerProducts(brokerId);

	const { data: signedConventions, isLoading: isLoadingSignedConventions } =
		useGetSocietyFiles(brokerId, "CONVENTION_SIGNED");

	const { data: brokerUsers } = useGetBrokerUsers(brokerId);

	if (brokerId <= 0) {
		return <div>Invalid broker ID</div>;
	}

	const signatoryUser =
		brokerUsers?.find((user) => user.isSignatory === true) || brokerUsers?.[0];
	const signatoryName = signatoryUser
		? `${signatoryUser.firstname} ${signatoryUser.lastname}`
		: "Non défini";

	const handleConventionClick = async (doc: {
		id: string;
		bucket: string;
		key: string;
		name: string;
		mimeType: string;
	}): Promise<void> => {
		try {
			const url = await getDocumentUrl({
				bucket: doc.bucket,
				key: doc.key,
			});

			if (url) {
				selectDocument(url, doc.id, doc.mimeType, doc.name);
			}
		} catch (error) {
			console.error(
				"Erreur lors de la récupération de l'URL du document:",
				error,
			);
			throw error;
		}
	};

	// Transform signed conventions data to match the table format
	const conventionTableData = (signedConventions || []).map((file) => {
		const metadata = file.file.fileData?.metadata as
			| Record<string, unknown>
			| undefined;
		const signedAt = metadata?.signedAt as string | undefined;

		return {
			name: file.file.name,
			date: signedAt
				? formatSimpleDate(signedAt)
				: formatSimpleDate(file.createdAt),
			signatory: signatoryName,
			status: "Signé",
			document: {
				id: file.file.id,
				bucket: file.file.bucket,
				key: file.file.key,
				name: file.file.name,
				mimeType: file.file.mimeType,
			},
			onDocumentClick: handleConventionClick,
		};
	});

	return (
		<div className="flex flex-col gap-4">
			<Separator />
			<div className="flex flex-col gap-4 pt-2 pb-3 w-full">
				<div className="text-text-main-blue  font-semibold">
					Convention Apporteur
				</div>
				<DataTable
					columns={contributorAgreementColumns}
					data={conventionTableData}
					noResultsText="Aucune convention signée trouvée"
					icon={FileWarning}
					variant="static-clean"
					isLoading={isLoadingSignedConventions}
				/>
			</div>
			<Separator />
			<div className="flex flex-col gap-4 pt-2 pb-3 w-full">
				<div className="text-text-main-blue  font-semibold">
					Historique du partenariat
				</div>
				<DataTable
					columns={partnershipHistoryColumns}
					data={[]}
					noResultsText="En cours de développement"
					icon={Construction}
					variant="static-clean"
				/>
			</div>
			<Separator />
			<div className="flex flex-col gap-4 pt-2 pb-3 w-full">
				<div className="text-text-main-blue  font-semibold">Produits</div>
				<DataTable
					columns={agreementProductsColumns}
					data={products || []}
					noResultsText="Aucun produit trouvé"
					icon={FileWarning}
					variant="static-clean"
					isLoading={isLoadingProducts}
				/>
			</div>
		</div>
	);
};

export default AgreementTab;
